// Shared panel scaffolding used by feature-specific overlays (cases, casting,
// coven, economy, etc). Every panel shares the same modal shell: full-screen
// backdrop, title-bar with × close button, Esc-dismiss, click-backdrop-dismiss.
// A single render callback receives the latest ClientState on every update.

import type { ClientStore, ClientState } from "../state.js";

export interface PanelHandle {
  close(): void;
  refresh(): void;
}

export function openPanel(params: {
  id: string;
  title: string;
  store: ClientStore;
  className?: string;
  render: (state: ClientState, refresh: () => void) => string;
  bind?: (root: HTMLElement, state: ClientState, close: () => void, refresh: () => void) => void;
}): PanelHandle {
  const existing = document.getElementById(params.id);
  if (existing) existing.remove();
  const overlay = document.createElement("div");
  overlay.id = params.id;
  overlay.className = `panel-overlay ${params.className ?? ""}`.trim();
  document.body.appendChild(overlay);

  let unsub: (() => void) | null = null;

  function close(): void {
    if (unsub) { unsub(); unsub = null; }
    document.removeEventListener("keydown", onKey);
    overlay.remove();
  }
  function onKey(ev: KeyboardEvent): void { if (ev.key === "Escape") close(); }

  function paint(state: ClientState): void {
    overlay.innerHTML = `
      <div class="panel-modal">
        <header class="panel-header">
          <h2>${escape(params.title)}</h2>
          <button class="panel-close" title="Close (Esc)">×</button>
        </header>
        <section class="panel-body">${params.render(state, refresh)}</section>
      </div>
    `;
    overlay.querySelector<HTMLButtonElement>(".panel-close")?.addEventListener("click", close);
    overlay.addEventListener("click", (ev) => { if (ev.target === overlay) close(); });
    if (params.bind) params.bind(overlay, state, close, refresh);
  }
  function refresh(): void { paint(params.store.get()); }

  unsub = params.store.subscribe(paint);
  document.addEventListener("keydown", onKey);
  paint(params.store.get());
  return { close, refresh };
}

export function escape(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]!));
}

/** Common panel CSS — injected once on first use. */
export function ensurePanelStyles(): void {
  if (document.getElementById("panel-shared-styles")) return;
  const s = document.createElement("style");
  s.id = "panel-shared-styles";
  s.textContent = `
    .panel-overlay { position: fixed; inset: 0; z-index: 1050; background: rgba(0,0,0,0.72); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; padding: 24px; overflow-y: auto; }
    .panel-modal { width: min(900px, 100%); max-height: calc(100vh - 48px); overflow-y: auto; background: var(--panel); border: 1px solid var(--gold); border-radius: 10px; box-shadow: 0 12px 48px rgba(0,0,0,0.6); padding: 20px 24px; }
    .panel-header { display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid var(--line); padding-bottom: 10px; margin-bottom: 16px; }
    .panel-header h2 { color: var(--gold); margin: 0; font-size: 18px; }
    .panel-close { background: transparent; border: 1px solid var(--dim); color: var(--dim); width: 28px; height: 28px; border-radius: 4px; cursor: pointer; font-size: 16px; }
    .panel-close:hover { background: var(--red); color: #07080c; border-color: var(--red); }
    .panel-body { font-size: 13px; line-height: 1.55; }
    .panel-body button { background: var(--panel); border: 1px solid var(--gold); color: var(--gold); padding: 4px 10px; border-radius: 4px; cursor: pointer; font-size: 12px; }
    .panel-body button:hover { background: var(--gold); color: #07080c; }
    .panel-body button:disabled { opacity: 0.4; cursor: not-allowed; }
    .panel-body input[type=text], .panel-body textarea, .panel-body select { background: #0c0e14; border: 1px solid var(--line); color: var(--txt); padding: 6px 8px; border-radius: 4px; font: inherit; }
    .panel-body textarea { width: 100%; min-height: 60px; resize: vertical; }
    .panel-body input[type=text] { width: 100%; }
    .panel-body table { width: 100%; border-collapse: collapse; }
    .panel-body th, .panel-body td { text-align: left; padding: 6px 8px; border-bottom: 1px solid var(--line); }
    .panel-body th { color: var(--dim); font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
    .panel-tabs { display: flex; gap: 8px; border-bottom: 1px solid var(--line); margin-bottom: 12px; padding-bottom: 0; }
    .panel-tab { padding: 8px 14px; cursor: pointer; color: var(--dim); border: none; background: transparent; border-bottom: 2px solid transparent; }
    .panel-tab.active { color: var(--gold); border-bottom-color: var(--gold); }
    .panel-row-alt tr:nth-child(even) { background: rgba(255,255,255,0.015); }
    .panel-section-title { color: var(--gold); font-size: 13px; font-weight: 600; margin: 12px 0 6px; letter-spacing: 0.04em; }
    .panel-hint { color: var(--dim); font-size: 11px; margin: 4px 0 8px; }

    /* Mobile: full-viewport panel, larger hit targets, iOS-safe inputs. */
    @media (max-width: 720px) {
      .panel-overlay { padding: 6px; align-items: flex-start; }
      .panel-modal { width: 100%; max-height: calc(100vh - 12px); border-radius: 6px; padding: 12px 14px; }
      .panel-header h2 { font-size: 16px; }
      .panel-close { width: 40px; height: 40px; font-size: 18px; }
      .panel-body { font-size: 14px; }
      .panel-body button { min-height: 42px; min-width: 42px; font-size: 14px; padding: 8px 12px; }
      .panel-body input[type=text], .panel-body textarea, .panel-body select { min-height: 42px; font-size: 16px; padding: 8px 10px; }
      .panel-body th, .panel-body td { padding: 8px 6px; }
      .panel-tabs { overflow-x: auto; flex-wrap: nowrap; -webkit-overflow-scrolling: touch; }
      .panel-tab { min-height: 42px; white-space: nowrap; padding: 10px 12px; }
    }
  `;
  document.head.appendChild(s);
}
